/**
 * ReviewItemsStep.js
 *
 * Step 4: Review and edit extracted items.
 */

import React, { useState } from 'react';
import {
  GripVertical,
  Trash2,
  Plus,
  AlertTriangle,
  Check,
  X,
  Edit2,
  BookOpen,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const ReviewItemsStep = ({
  items = [],
  extractionResult,
  onUpdateItem,
  onAddItem,
  onRemoveItem,
  onReorderItems
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [newItemText, setNewItemText] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Start editing an item
  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditText(item.text);
  };

  // Save edit
  const handleSaveEdit = (itemId) => {
    if (editText.trim()) {
      onUpdateItem(itemId, { text: editText.trim() });
    }
    setEditingId(null);
    setEditText('');
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  // Add new item
  const handleAddItem = () => {
    if (newItemText.trim()) {
      onAddItem({ text: newItemText.trim() });
      setNewItemText('');
      setShowAddForm(false);
    }
  };

  // Toggle required status
  const handleToggleRequired = (itemId, currentRequired) => {
    onUpdateItem(itemId, { required: !currentRequired });
  };

  // Toggle item expansion
  const toggleExpand = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  // Confidence indicator
  const ConfidenceBadge = ({ confidence }) => {
    if (!confidence && confidence !== 0) return null;

    const percent = Math.round(confidence * 100);
    let colorClass = 'bg-green-100 text-green-700';
    if (percent < 70) colorClass = 'bg-amber-100 text-amber-700';
    if (percent < 50) colorClass = 'bg-red-100 text-red-700';

    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${colorClass}`}>
        {percent}%
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Summary */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Review Extracted Items</h3>
          <p className="text-sm text-slate-500">
            {items.length} items found • Edit or remove as needed
          </p>
        </div>

        {extractionResult?.metadata?.overallConfidence && (
          <div className="text-right">
            <p className="text-xs text-slate-500">Confidence</p>
            <p className="text-lg font-semibold text-slate-800">
              {Math.round(extractionResult.metadata.overallConfidence * 100)}%
            </p>
          </div>
        )}
      </div>

      {/* Title from extraction */}
      {extractionResult?.title && (
        <div className="mb-4 p-3 bg-slate-50 rounded-lg">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Detected Title</p>
          <p className="font-medium text-slate-800">{extractionResult.title}</p>
        </div>
      )}

      {/* Items list */}
      <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
        {items.map((item, index) => {
          const isEditing = editingId === item.id;
          const isExpanded = expandedItems.has(item.id);
          const hasLowConfidence = item.confidence && item.confidence < 0.7;

          return (
            <div
              key={item.id}
              className={`
                border rounded-lg transition-colors
                ${hasLowConfidence ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}
                ${isEditing ? 'ring-2 ring-cyan-500' : ''}
              `}
            >
              <div className="flex items-start gap-2 p-3">
                {/* Drag handle */}
                <button className="p-1 text-slate-400 hover:text-slate-600 cursor-grab">
                  <GripVertical className="h-4 w-4" />
                </button>

                {/* Required toggle */}
                <button
                  onClick={() => handleToggleRequired(item.id, item.required)}
                  className={`
                    flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center
                    ${item.required
                      ? 'bg-cyan-500 border-cyan-500 text-white'
                      : 'border-slate-300 hover:border-slate-400'
                    }
                  `}
                  title={item.required ? 'Required (click to make optional)' : 'Optional (click to make required)'}
                >
                  {item.required && <Check className="h-3 w-3" />}
                </button>

                {/* Item content */}
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit(item.id);
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                        className="flex-1 px-2 py-1 border border-cyan-300 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveEdit(item.id)}
                        className="p-1 text-green-600 hover:bg-green-100 rounded"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-800">{item.text}</p>
                  )}

                  {/* Regulatory ref and notes */}
                  {(item.regulatoryRef || item.notes) && isExpanded && (
                    <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                      {item.regulatoryRef && (
                        <p className="text-xs text-slate-500">
                          <BookOpen className="h-3 w-3 inline mr-1" />
                          {item.regulatoryRef}
                        </p>
                      )}
                      {item.notes && (
                        <p className="text-xs text-slate-500 italic">{item.notes}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Badges and actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {hasLowConfidence && (
                    <AlertTriangle className="h-4 w-4 text-amber-500" title="Low confidence - review this item" />
                  )}
                  <ConfidenceBadge confidence={item.confidence} />

                  {(item.regulatoryRef || item.notes) && (
                    <button
                      onClick={() => toggleExpand(item.id)}
                      className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                    >
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  )}

                  {!isEditing && (
                    <>
                      <button
                        onClick={() => handleStartEdit(item)}
                        className="p-1 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onRemoveItem(item.id)}
                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <p>No items extracted. Add items manually below.</p>
          </div>
        )}
      </div>

      {/* Add item form */}
      {showAddForm ? (
        <div className="flex items-center gap-2 p-3 border border-dashed border-cyan-300 rounded-lg bg-cyan-50">
          <input
            type="text"
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddItem();
              if (e.key === 'Escape') {
                setShowAddForm(false);
                setNewItemText('');
              }
            }}
            placeholder="Enter new item text..."
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            autoFocus
          />
          <button
            onClick={handleAddItem}
            disabled={!newItemText.trim()}
            className="px-4 py-2 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            Add
          </button>
          <button
            onClick={() => {
              setShowAddForm(false);
              setNewItemText('');
            }}
            className="p-2 text-slate-400 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 p-3 border border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-cyan-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Item Manually
        </button>
      )}
    </div>
  );
};

export default ReviewItemsStep;
